import {
  SUPERSTACK_TOKENS,
  SUPPORTED_TOKEN_CONTRACTS,
  SupportedTokenName,
} from "@mezo/sdk"

import { ETHEREUM_NETWORK } from "#/shared/constants"
import { calculateMaxStbtcMintAmount } from "#/shared/utils"

import createDappAsyncThunk from "../asyncThunk"
import {
  selectSuperstackEligibleDeposits,
  selectTotalMintedAmount,
} from "../selectors/portal"
import { selectMintingFeeInfo } from "../selectors/receipt-token"
import {
  setDeposits,
  setHasLoadedTransactions,
  setWithdrawals,
} from "../slices/portal"
import {
  setFeeInfo,
  setMaxPossibleTotalMintAmount,
} from "../slices/receipt-token"
import {
  setHasLoadedWalletBalances,
  setTokenAllowance,
  setWalletBalances,
} from "../slices/wallet"

export const fetchWalletBalances = createDappAsyncThunk(
  "portal/fetchWalletBalances",
  async (_, { dispatch, extra: { sdk } }) => {
    const supportedTokenBalances = await sdk.mezo.getSupportedTokenBalances()
    dispatch(setWalletBalances(supportedTokenBalances))
    dispatch(setHasLoadedWalletBalances(true))
  },
)

export const fetchAllTransactions = createDappAsyncThunk(
  "portal/fetchAllDeposits",
  async (_, { dispatch, extra: { sdk } }) => {
    const transactions = await sdk.mezo.getPortalTransactions()

    // If no deposits or connection error do not update deposits in redux
    if (!transactions) return

    const { deposits, withdrawals } = transactions

    dispatch(setDeposits(deposits))
    dispatch(setWithdrawals(withdrawals))
    dispatch(setHasLoadedTransactions(true))
  },
)

export const fetchTokenAllowances = createDappAsyncThunk(
  "portal/fetchTokenAllowances",
  async (_, { dispatch, extra: { sdk } }) => {
    const tokenAllowances = await sdk.mezo.getTokenAllowance()
    dispatch(setTokenAllowance(tokenAllowances))
  },
)

export const fetchReceiptTokenFeeInfo = createDappAsyncThunk(
  "receipt-token/fetchReceiptTokenFeeInfo",
  async (_, { extra: { sdk }, dispatch }) => {
    await Promise.all(
      SUPERSTACK_TOKENS.map(async (token: SupportedTokenName) => {
        const { address, abi } = SUPPORTED_TOKEN_CONTRACTS[token]
        const tokenForDeposit = { address: address[ETHEREUM_NETWORK], abi }

        const feeInfo =
          await sdk.mezo.chain?.receiptTokenModule.getMintFeeInfo(
            tokenForDeposit,
          )

        if (feeInfo) {
          dispatch(setFeeInfo({ token, feeInfo }))
        }
      }),
    )
  },
)

export const fetchMaxTotalPossibleMintAmount = createDappAsyncThunk(
  "receipt-token/fetchMaxTotalPossibleMintAmount",
  async (_, { getState, dispatch, extra: { sdk } }): Promise<void> => {
    const eligibleDepositsFromStore =
      selectSuperstackEligibleDeposits(getState())

    const totalMintedAmount = selectTotalMintedAmount(getState())

    const mintingFeeInfo = selectMintingFeeInfo(getState())

    const eligibleDepositsIdFromStore = eligibleDepositsFromStore.map(
      (deposit) => deposit.depositId,
    )

    const depositsFeeData =
      await sdk.mezo.chain?.depositModule.getFeeDataForChainDeposits()

    const finalDepositsFeeData = depositsFeeData
      ?.filter((depositFeeData) =>
        eligibleDepositsIdFromStore.includes(Number(depositFeeData.depositId)),
      )
      .map((depositFeeData) => ({
        ...depositFeeData,
        depositId: Number(depositFeeData.depositId),
      }))

    if (!finalDepositsFeeData) return

    const max = finalDepositsFeeData.reduce((sum, depositFeeData) => {
      const depositFromStore = eligibleDepositsFromStore.find(
        (deposit) => deposit.depositId === depositFeeData.depositId,
      )

      if (!depositFromStore) return sum

      const mintingFeeInfoForToken = mintingFeeInfo[depositFromStore.token]

      if (!mintingFeeInfoForToken) return sum

      const maxMintAmount = calculateMaxStbtcMintAmount(
        depositFromStore.amount,
        depositFromStore.mintedAmount,
        mintingFeeInfoForToken,
        depositFeeData,
      )

      return sum + maxMintAmount
    }, totalMintedAmount)

    dispatch(setMaxPossibleTotalMintAmount(max))
  },
)
